import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';

// Components
import { Grid, Col } from '../../components/Grid';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import Filters from '../../components/Filters';
import Pagination from '../../components/Pagination';
import ResultsTable from '../../components/ResultsTable';

// Services, Config etc.
import { STORES, PURCHASE_ITEM_TYPES, PURCHASE_PAYMENT_METHODS } from '../../config';
import { fetchPurchases } from '../../services/purchases';
import { useFlash } from '../../components/FlashMessage';

// Styles
import styles from './Purchases.module.scss';

const Purchases = ({ auth }) => {
  const { search } = useLocation();
  const history = useHistory();
  const [showFilters, setShowFilters] = useState(false);
  const [purchases, setPurchases] = useState(null);
  const { showFlash } = useFlash();

  // Perform request whenever the query string changes
  useEffect(() => {
    const defaultQuery = { sortBy: 'purchaseDate', order: 'asc', page: 1 };
    const currentQuery = queryString.parse(search);

    fetchPurchases({
      ...defaultQuery,
      ...currentQuery
    })
      .then(setPurchases)
      .catch(error => showFlash('error', error));
  }, [search, showFlash]);

  return (
    <>
      <Grid>
        <Col>
          <Button format="secondary" onClick={() => setShowFilters(true)}>
            <Icon icon="filter" /> Show filters
          </Button>
        </Col>
        <Col width="auto">
          <h1>Purchases</h1>
        </Col>
        <Col className="range-right">
          <Button format="primary" href="/purchases/add">
            + Add Purchase
          </Button>
          {auth.user.role === 'admin' && (
            <Button format="secondary" href="/api/purchases/export" download>
              Export
            </Button>
          )}
        </Col>
      </Grid>

      <Filters
        open={showFilters}
        onClose={() => setShowFilters(false)}
        options={[
          ...(auth.user.role !== 'staff'
            ? [
                {
                  label: 'Store',
                  name: 'store',
                  type: 'multiple',
                  options: STORES
                }
              ]
            : []),
          {
            label: 'Item Type',
            name: 'itemType',
            type: 'multiple',
            options: PURCHASE_ITEM_TYPES
          },
          {
            label: 'Payment Method',
            name: 'paymentMethod',
            type: 'multiple',
            options: PURCHASE_PAYMENT_METHODS
          },
          {
            label: 'Tax Free',
            name: 'taxFree',
            type: 'multiple',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]
          },
          {
            label: 'Complimentary Gift',
            name: 'complimentaryGift',
            type: 'multiple',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]
          },
          {
            label: 'Insurance Taken',
            name: 'insuranceTaken',
            type: 'multiple',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]
          }
        ]}
      />

      {purchases && purchases.records.length > 0 && (
        <>
          <ResultsTable
            config={{
              columns: [
                {
                  key: 'stockNumber',
                  label: 'Stock No.',
                  sortable: true,
                  className: styles.stock
                },
                {
                  key: 'invoiceNumber',
                  label: 'Invoice No.',
                  sortable: true,
                  className: styles.invoice
                },
                {
                  key: 'description',
                  label: 'Description',
                  sortable: true
                },
                {
                  key: 'itemType',
                  label: 'Item Type',
                  sortable: true,
                  className: styles.item
                },
                {
                  key: 'invoicePrice',
                  label: 'Invoice Price',
                  sortable: true,
                  format: 'money',
                  className: styles.price
                },
                {
                  key: 'purchaseDate',
                  label: 'Purchase Date',
                  sortable: true,
                  format: 'date',
                  className: styles.date
                }
              ],
              onRowClick: _id => history.push(`/purchases/${_id}`)
            }}
            rows={purchases.records}
          />
          <Pagination
            total={purchases.total}
            perPage={purchases.perPage}
            currentPage={purchases.page}
          />
        </>
      )}

      {purchases && purchases.records.length < 1 && (
        <p className="lg range-center">No purchases found mathcing your query</p>
      )}
    </>
  );
};

export default Purchases;
